import React, { useCallback, useState } from 'react';
import moment from 'moment';
import ArmorAvatar from './ArmorAvatar';
import ProgressCelebrations from './ProgressCelebrations';
import ArmorPopover from './ArmorPopover';
import LeaderboardPopover from './LeaderboardPopover';
import BossBattle from './BossBattle';
import { formatHours } from '../utils/events';
import { useProgress } from '../hooks/useProgress';
import { useProgressFx } from '../hooks/useProgressFx';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useBossBattle } from '../hooks/useBossBattle';
import { XP_DAILY_QUEST, XP_WEEKLY_QUEST, XP_PER_HOUR, xpForLevel } from '../utils/progress';

const GEAR_PATH = 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z';
const nfEn = new Intl.NumberFormat('en-GB');
const total = (t) => (t ? t.billable + t.nonBillable : 0);

const viewLabel = (currentView, currentDate) => {
  if (currentView === 'day') return moment(currentDate).format('D ddd');
  if (currentView === 'month' || currentView === 'list') return moment(currentDate).format('MMMM');
  return `Week ${moment(currentDate).isoWeek()}`;
};

function Stat({ label, value, sub, minWidth, onClick, title }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      title={title}
      className={`flex flex-col text-left ${onClick ? 'hover:opacity-80 transition-opacity' : ''}`}
      style={{ gap: 1, minWidth }}
    >
      <span className="uppercase font-semibold text-white/50" style={{ fontSize: 11, lineHeight: '14px', letterSpacing: '.08em' }}>{label}</span>
      <span className="font-bold tabular-nums" style={{ fontSize: 22, lineHeight: '26px' }}>{value}</span>
      <span className="text-white/60" style={{ fontSize: 12, lineHeight: '16px' }}>{sub}</span>
    </Tag>
  );
}

const Divider = () => <div style={{ width: 1, height: 50, background: '#273C61' }} />;

function Quest({ done, text, value, popKey }) {
  return (
    <div className="flex items-center" style={{ gap: 8 }}>
      <span
        key={done ? popKey : 'open'}
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 15, height: 15, border: '2px solid #FF7800', borderRadius: 3, boxSizing: 'border-box',
          background: done ? '#FF7800' : 'transparent', animation: done ? 'zrmPop .4s' : undefined,
        }}
      >
        {done && (
          <svg width="9" height="9" viewBox="0 0 20 20" fill="#0D1A27"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
        )}
      </span>
      <span className="flex-1 text-white/85" style={{ fontSize: 12 }}>{text}</span>
      <span className="tabular-nums text-white/60" style={{ fontSize: 12 }}>{value}</span>
    </div>
  );
}

export default function DesktopFooter({
  events,
  totalsCurrentView,
  totalsCurrentViewPeople,
  totalsYTD,
  totalsYTDPeople,
  currentView,
  currentDate,
  customRangeData,
  isLoadingCustomRange,
  onCustomDateRangeClick,
  onClearCustomRange,
  bonus,
  renderBonusPeriodSettings,
  isOutlookSyncEvent,
  isBillableEvent,
  mutationAtRef,
  projects,
}) {
  const [people, setPeople] = useState(false);
  const [showBonusSettings, setShowBonusSettings] = useState(false);
  // Kun én popover ad gangen: 'armor' | 'board' | null.
  const [popover, setPopover] = useState(null);
  const context = people ? 'people' : 'projects';

  const { progress } = useProgress({
    events, weekNorm: bonus?.ugenorm, isOutlookSyncEvent, isBillableEvent, email: bonus?.email,
  });
  const { entries, rank, reload: reloadLeaderboard } = useLeaderboard();
  const boss = useBossBattle({ events, projects, isOutlookSyncEvent, mutationAtRef });
  const closePopover = useCallback(() => setPopover(null), []);

  const weekTotals = people ? totalsCurrentViewPeople : totalsCurrentView;
  const ytdTotals = people ? totalsYTDPeople : totalsYTD;
  const customHere = customRangeData && (customRangeData.range?.context || 'projects') === context;
  const ytdValue = customHere ? customRangeData.current.total : total(ytdTotals);
  const ytdBillable = customHere ? customRangeData.current.billable : ytdTotals?.billable || 0;

  const { displayed, fx, banner, burstKey, pulse } = useProgressFx({
    targets: { week: total(weekTotals), ytd: ytdValue, xp: progress.xp },
    progress,
    mutationAtRef,
  });

  const suffix = people ? ' · People' : '';
  const weekLabel = viewLabel(currentView, currentDate) + suffix;
  const hasComparison = customHere && customRangeData.previous && customRangeData.differences;
  const xpPct = Math.min(100, Math.max(0, ((displayed.xp - xpForLevel(progress.level)) / (xpForLevel(progress.level + 1) - xpForLevel(progress.level))) * 100));
  const floatStyle = { position: 'absolute', top: -8, left: 0, fontSize: 12, fontWeight: 700, color: '#FF7800', animation: 'zrmFloat 1.2s ease-out forwards', pointerEvents: 'none', whiteSpace: 'nowrap' };
  const pct = bonus?.procentTilDato;
  const hasBonus = bonus && typeof pct === 'number';
  const norm = progress.weekNorm;
  const dayNorm = progress.dayNorm;

  const menuItem = 'block w-full text-left text-xs px-2 py-1 rounded hover:bg-[#273C61]';

  return (
    <div
      className="hidden lg:flex fixed bottom-0 left-0 right-0 items-center text-white"
      style={{ height: 95, padding: '12px 20px', background: '#0D1A27', borderTop: '1px solid #273C61', boxSizing: 'border-box', gap: 22, zIndex: 2000, fontFamily: "'Open Sans', sans-serif" }}
    >
      {/* Toggle */}
      <div className="flex flex-col flex-none" style={{ gap: 2 }}>
        {[['Projects', false], ['People', true]].map(([name, val]) => (
          <button
            key={name}
            onClick={() => setPeople(val)}
            className="rounded"
            style={{ padding: '2px 8px', fontSize: 11, lineHeight: '16px', fontWeight: 700, background: people === val ? '#273C61' : 'transparent', color: people === val ? '#fff' : 'rgba(255,255,255,.5)' }}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="relative flex-none">
        <Stat label={weekLabel} value={formatHours(displayed.week)} sub={`${formatHours(weekTotals?.billable || 0)} billable`} minWidth={120} />
        {fx.filter((f) => f.kind === 'hours').map((f) => <span key={f.id} style={floatStyle}>{f.text}</span>)}
      </div>

      {customHere && (
        <div className="flex-none">
          <Stat
            label={customRangeData.label}
            value={formatHours(displayed.ytd)}
            sub={hasComparison
              ? `${formatHours(ytdBillable)} billable · vs last year ${customRangeData.differences.total >= 0 ? '+' : ''}${formatHours(customRangeData.differences.total)}`
              : `${formatHours(ytdBillable)} billable`}
            minWidth={160}
            onClick={onClearCustomRange}
            title="Click to remove custom range"
          />
        </div>
      )}
      {isLoadingCustomRange && <span className="text-white/60" style={{ fontSize: 11 }}>Calculating...</span>}

      {/* Bonus + Leaderboard + tandhjul */}
      <div
        className="relative flex items-center flex-none"
        style={{ gap: 14, height: 71, padding: '0 14px', borderRadius: 8, background: '#111e2e', border: '1px solid #273C61', boxSizing: 'border-box' }}
        title="Bonus is calculated in paymaster, the same way for everyone"
      >
        {hasBonus && (
          <>
            <div className="flex flex-col" style={{ gap: 1 }}>
              <span className="uppercase font-semibold" style={{ fontSize: 11, lineHeight: '14px', letterSpacing: '.08em', color: 'rgba(255,255,255,.5)' }}>Bonus</span>
              <span className="font-bold tabular-nums" style={{ fontSize: 26, lineHeight: '28px', color: pct >= 0 ? '#5CBE87' : '#fff' }}>{pct >= 0 ? '+' : ''}{pct.toFixed(1)}%</span>
              {typeof bonus.faktiskT === 'number' && typeof bonus.forventetT === 'number' && (
                <span className="whitespace-nowrap" style={{ fontSize: 11, lineHeight: '14px', color: 'rgba(255,255,255,.6)' }}>
                  {formatHours(bonus.faktiskT)} / {formatHours(bonus.forventetT)} expected
                </span>
              )}
            </div>
            <div style={{ width: 1, height: 40, background: '#273C61' }} />
          </>
        )}
        <button
          onClick={() => { if (popover !== 'board') reloadLeaderboard(); setPopover((p) => (p === 'board' ? null : 'board')); }}
          className="flex flex-col items-start hover:bg-[#273C61]"
          style={{ gap: 2, padding: '8px 12px', borderRadius: 6, border: '1px solid #273C61', background: 'transparent', color: '#fff' }}
          aria-label="Leaderboard"
        >
          <span className="flex items-center uppercase font-bold" style={{ gap: 6, fontSize: 11, lineHeight: '14px', letterSpacing: '.08em' }}>
            <svg width="14" height="14" fill="none" stroke="#FF7800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM17 5h3v2a3 3 0 01-3 3M7 5H4v2a3 3 0 003 3" />
            </svg>
            Leaderboard
          </span>
          {rank && (
            <span style={{ fontSize: 12, lineHeight: '16px', color: 'rgba(255,255,255,.6)' }}>
              You are <b className="text-white">#{rank.rank}</b> of {rank.of}
            </span>
          )}
        </button>
        <div className="relative self-start" style={{ marginTop: 2 }}>
          <button onClick={() => setShowBonusSettings((v) => !v)} className="text-white/40 hover:text-white" title="Bonus and custom period" aria-label="Bonus and custom period">
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={GEAR_PATH} />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          {showBonusSettings && (
            <div className="absolute right-0 flex flex-col" style={{ bottom: 'calc(100% + 18px)', gap: 6, zIndex: 2002 }}>
              <div className="bg-[#111e2e] border border-[#273C61] rounded p-1" style={{ minWidth: 200 }}>
                <button
                  className={menuItem}
                  onClick={() => { setShowBonusSettings(false); if (customHere) onClearCustomRange(); else onCustomDateRangeClick(context); }}
                >
                  {customHere ? 'Remove custom period' : 'Custom period and last-year comparison...'}
                </button>
              </div>
              {hasBonus && renderBonusPeriodSettings()}
            </div>
          )}
        </div>
        {popover === 'board' && <LeaderboardPopover entries={entries} year={moment(currentDate).year()} onClose={closePopover} />}
      </div>

      <Divider />

      {/* Avatar + XP */}
      <div className="relative flex items-center flex-none" style={{ gap: 16 }}>
        <div className="relative flex-none">
          <button
            key={`ring-${pulse.ring}`}
            onClick={() => setPopover((p) => (p === 'armor' ? null : 'armor'))}
            className="relative block hover:border-[#FF7800]"
            style={{ width: 60, height: 70, borderRadius: 8, background: '#111e2e', border: '1px solid #273C61', cursor: 'pointer', animation: pulse.ring ? 'zrmRing .9s ease-out, zrmPop .4s' : undefined }}
            title="Click to see how you level up"
            aria-label="Your armor"
          >
            <div className="zrm-bob" style={{ position: 'absolute', left: 5, top: 1 }}>
              <ArmorAvatar stage={progress.stage} cell={3} />
            </div>
            {pulse.flash > 0 && (
              <div key={`flash-${pulse.flash}`} style={{ position: 'absolute', inset: 0, borderRadius: 8, background: '#fff', opacity: 0, animation: 'zrmFlash .7s ease-out', pointerEvents: 'none' }} />
            )}
            <span
              className="absolute text-center font-bold"
              style={{ right: -7, bottom: -7, minWidth: 22, height: 18, padding: '0 5px', borderRadius: 9, background: '#FF7800', color: '#0D1A27', fontSize: 10, lineHeight: '18px', boxSizing: 'border-box' }}
            >
              {progress.level}
            </span>
          </button>
          {popover === 'armor' && <ArmorPopover progress={progress} onClose={closePopover} />}
        </div>

        <div className="relative flex flex-col flex-none" style={{ width: 280, gap: 5 }}>
          <div className="flex items-baseline justify-between">
            <span>
              <span className="font-bold" style={{ fontSize: 14, lineHeight: '18px' }}>{progress.stageName}</span>
              <span className="font-semibold text-white/50" style={{ fontSize: 11, marginLeft: 6 }}>Lvl {progress.level}</span>
            </span>
            <span className="tabular-nums text-white/60 whitespace-nowrap" style={{ fontSize: 11 }}>
              {nfEn.format(Math.round(displayed.xp))} / {nfEn.format(xpForLevel(progress.level + 1))} XP
            </span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: '#273C61' }}>
            <div key={`glow-${pulse.glow}`} style={{ height: 8, borderRadius: 4, background: '#94C2FF', width: `${xpPct}%`, animation: pulse.glow ? 'zrmGlow 1s' : undefined }} />
          </div>
          <div className="text-white/60" style={{ fontSize: 11 }}>
            {progress.nextStageName ? (
              <>
                Next armor: <span className="text-white font-semibold">{progress.nextStageName} at Lvl {progress.nextStageLevel}</span>
                {' '}({formatHours(progress.hoursToNextStage)} to go)
              </>
            ) : 'Max armor reached'}
          </div>
          {fx.filter((f) => f.kind === 'xp').map((f) => <span key={f.id} style={{ ...floatStyle, left: 'auto', right: 0 }}>{f.text}</span>)}
        </div>
        <ProgressCelebrations banner={banner} burstKey={burstKey} />
      </div>

      <Divider />

      {/* Quests */}
      <div className="flex flex-col flex-none" style={{ width: 240, gap: 9 }}>
        <Quest done={progress.dayDone} popKey={pulse.pop} text={`Daily quest · log ${formatHours(dayNorm)}`} value={formatHours(progress.dayHours)} />
        <Quest done={progress.weekDone} popKey={pulse.pop} text={`Weekly quest · log ${formatHours(norm)}`} value={formatHours(progress.weekHours)} />
        <div className="text-white/40" style={{ fontSize: 10 }}>
          Daily +{XP_DAILY_QUEST} XP {'·'} Weekly +{nfEn.format(XP_WEEKLY_QUEST)} XP {'·'} 1h = {XP_PER_HOUR} XP
        </div>
      </div>

      <Divider />

      <BossBattle boss={boss} stage={progress.stage} />

      <div className="flex-1" />
    </div>
  );
}
